import MaterialIcons from '@expo/vector-icons/MaterialIcons';
import { Image } from 'expo-image';
import * as Linking from 'expo-linking';
import { router, Slot, usePathname } from 'expo-router';
import { useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  View,
  useWindowDimensions,
} from 'react-native';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';

import { Fonts } from '@/constants/theme';
import { hasCompletedAdminOnboarding } from '@/lib/admin-onboarding';
import { getWebsiteOnboardingUrl } from '@/lib/auth-routing';
import { useAuth } from '@/providers/auth-provider';

type AdminRoute =
  | '/admin'
  | '/admin/index'
  | '/admin/account'
  | '/admin/assignments'
  | '/admin/classes'
  | '/admin/students'
  | '/admin/subjects'
  | '/admin/teachers';

const navItems = [
  { icon: 'home' as const, route: '/admin' as AdminRoute, title: 'Home' },
  { icon: 'domain' as const, route: '/admin/classes' as AdminRoute, title: 'Classes' },
  { icon: 'library-books' as const, route: '/admin/subjects' as AdminRoute, title: 'Subjects' },
  { icon: 'badge' as const, route: '/admin/teachers' as AdminRoute, title: 'Teachers' },
  { icon: 'school' as const, route: '/admin/students' as AdminRoute, title: 'Students' },
  { icon: 'assignment' as const, route: '/admin/assignments' as AdminRoute, title: 'Assign' },
];

const navSections = [
  {
    label: 'Platform',
    items: navItems.slice(0, 1),
  },
  {
    label: 'Management',
    items: navItems.slice(1),
  },
];

const brandLogoSource =
  Platform.OS === 'web'
    ? require('../../assets/images/mcqgen-logo-mark.webp')
    : require('../../assets/images/favicon.png');

function getShortName(value: string | null | undefined) {
  const trimmed = value?.trim();

  if (!trimmed) {
    return 'Admin';
  }

  return trimmed.split(' ')[0] ?? 'Admin';
}

function getProfilePhotoUrl(metadata: Record<string, unknown> | undefined) {
  const candidates = [
    metadata?.avatar_url,
    metadata?.picture,
    metadata?.photo_url,
    metadata?.profile_photo_url,
  ];

  for (const value of candidates) {
    if (typeof value === 'string' && value.trim().length > 0) {
      return value.trim();
    }
  }

  return null;
}

export default function AdminLayout() {
  const { profile, session } = useAuth();
  const pathname = usePathname();
  const insets = useSafeAreaInsets();
  const { width } = useWindowDimensions();
  const isDesktop = width >= 960;
  const [onboardingStatus, setOnboardingStatus] = useState<'checking' | 'ready' | 'blocked'>(
    'checking'
  );

  useEffect(() => {
    let isMounted = true;

    const loadOnboardingStatus = async () => {
      if (!profile?.id) {
        if (isMounted) {
          setOnboardingStatus('checking');
        }
        return;
      }

      const completed = await hasCompletedAdminOnboarding(profile.id);

      if (!isMounted) {
        return;
      }

      setOnboardingStatus(completed ? 'ready' : 'blocked');
    };

    void loadOnboardingStatus();

    return () => {
      isMounted = false;
    };
  }, [profile?.id]);

  useEffect(() => {
    if (onboardingStatus !== 'blocked') {
      return;
    }

    void Linking.openURL(getWebsiteOnboardingUrl('/admin', session));
  }, [onboardingStatus, session]);

  const activeTitle = useMemo(() => {
    const matched = navItems.find((item) =>
      item.route === '/admin'
        ? pathname === '/admin' || pathname === '/admin/index'
        : pathname === item.route || pathname.startsWith(`${item.route}/`)
    );

    if (matched) return matched.title;
    if (pathname.includes('/admin/account')) return 'Profile';
    return 'Admin';
  }, [pathname]);

  const goTo = (route: AdminRoute) => {
    router.push(route as never);
  };

  const isHome = pathname === '/admin' || pathname === '/admin/index';
  const adminName = getShortName(profile?.full_name);
  const profilePhotoUrl = getProfilePhotoUrl(
    session?.user.user_metadata as Record<string, unknown> | undefined
  );
  const isActiveRoute = (route: AdminRoute) =>
    route === '/admin'
      ? pathname === '/admin' || pathname === '/admin/index'
      : pathname === route || pathname.startsWith(`${route}/`);

  if (onboardingStatus !== 'ready') {
    return (
      <View style={styles.loadingShell}>
        <ActivityIndicator color="#173A2A" size="large" />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {isDesktop ? (
        <View style={styles.desktopShell}>
          <View style={styles.sidebar}>
            <View style={styles.sidebarTop}>
              <View style={styles.sidebarHeader}>
                <View style={styles.brandRow}>
                  <View style={styles.brandMark}>
                    <Image
                      accessible={false}
                      contentFit="contain"
                      source={brandLogoSource}
                      style={styles.brandLogo}
                    />
                  </View>
                  <View style={styles.brandText}>
                    <Text style={styles.brandName}>MCQGen</Text>
                    <Text style={styles.brandSub}>Admin workspace</Text>
                  </View>
                </View>
              </View>

              {navSections.map((section) => (
                <View key={section.label} style={styles.sidebarSection}>
                  <Text style={styles.sidebarLabel}>{section.label}</Text>
                  <View style={styles.navList}>
                    {section.items.map((item) => {
                      const isActive = isActiveRoute(item.route);

                      return (
                        <Pressable
                          key={item.route}
                          onPress={() => goTo(item.route)}
                          style={({ pressed }) => [
                            styles.sidebarItem,
                            isActive && styles.sidebarItemActive,
                            pressed && styles.sidebarItemPressed,
                          ]}>
                          <View
                            style={[
                              styles.sidebarItemIndicator,
                              isActive && styles.sidebarItemIndicatorActive,
                            ]}
                          />
                          <View
                            style={[
                              styles.sidebarIconWrap,
                              isActive && styles.sidebarIconWrapActive,
                            ]}>
                            <MaterialIcons
                              color={isActive ? '#17412E' : '#6E7E72'}
                              name={item.icon}
                              size={18}
                            />
                          </View>
                          <Text
                            style={[
                              styles.sidebarLabelText,
                              isActive && styles.sidebarLabelActive,
                            ]}>
                            {item.title}
                          </Text>
                        </Pressable>
                      );
                    })}
                  </View>
                </View>
              ))}
            </View>

            <Pressable
              onPress={() => goTo('/admin/account')}
              style={({ pressed }) => [
                styles.sidebarFooter,
                isActiveRoute('/admin/account') && styles.sidebarFooterActive,
                pressed && styles.sidebarItemPressed,
              ]}>
              {profilePhotoUrl ? (
                <Image
                  accessibilityLabel={`${profile?.full_name?.trim() || 'Administrator'} profile photo`}
                  contentFit="cover"
                  source={{ uri: profilePhotoUrl }}
                  style={styles.sidebarFooterPhoto}
                />
              ) : (
                <View style={styles.sidebarFooterAvatar}>
                  <Text style={styles.sidebarFooterAvatarText}>
                    {adminName.slice(0, 1).toUpperCase()}
                  </Text>
                </View>
              )}
              <View style={styles.sidebarFooterCopy}>
                <Text style={styles.sidebarFooterTitle} numberOfLines={1}>
                  {profile?.full_name?.trim() || 'Administrator'}
                </Text>
                <Text style={styles.sidebarFooterMeta} numberOfLines={1}>
                  {profile?.email || (isHome ? 'Overview and shortcuts' : activeTitle)}
                </Text>
              </View>
              <MaterialIcons color="#6B7280" name="more-horiz" size={18} />
            </Pressable>
          </View>

          <View style={styles.desktopContent}>
            {!isHome && (
              <View style={styles.desktopHeader}>
                <View>
                  <Text style={styles.desktopHeaderTitle} numberOfLines={1}>
                    {activeTitle}
                  </Text>
                  <Text style={styles.desktopHeaderSubtitle}>Admin workspace</Text>
                </View>
              </View>
            )}

            <View style={styles.desktopSlot}>
              <Slot />
            </View>
          </View>
        </View>
      ) : (
        <>
          {!isHome && (
            <SafeAreaView edges={['top']} style={styles.headerSafe}>
              <View style={styles.header}>
                <Text numberOfLines={1} style={styles.headerTitle}>
                  {activeTitle}
                </Text>
                <Text style={styles.headerSubtitle}>Admin workspace</Text>
              </View>
            </SafeAreaView>
          )}

          <View style={styles.content}>
            <Slot />
          </View>

          <SafeAreaView edges={['bottom']} style={styles.navSafe}>
            <View style={[styles.bottomNav, { marginBottom: Math.max(insets.bottom, 8) }]}>
              {navItems
                .filter((item) => item.route !== '/admin/account')
                .map((item) => {
                  const isActive = isActiveRoute(item.route);

                  return (
                    <Pressable
                      key={item.route}
                      onPress={() => goTo(item.route)}
                      style={({ pressed }) => [
                        styles.navItem,
                        isActive && styles.navItemActive,
                        pressed && styles.navItemPressed,
                      ]}>
                      <MaterialIcons
                        color={isActive ? '#173D22' : '#91A394'}
                        name={item.icon}
                        size={22}
                      />
                      <Text style={[styles.navLabel, isActive && styles.navLabelActive]}>
                        {item.title}
                      </Text>
                    </Pressable>
                  );
                })}
            </View>
          </SafeAreaView>
        </>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  loadingShell: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#EEF5EE',
  },
  container: {
    flex: 1,
    backgroundColor: '#EEF5EE',
  },
  desktopShell: {
    flex: 1,
    flexDirection: 'row',
    backgroundColor: '#EDF3EC',
  },
  sidebar: {
    width: 276,
    backgroundColor: '#FAFAFA',
    paddingHorizontal: 16,
    paddingVertical: 18,
    justifyContent: 'space-between',
    borderRightWidth: 1,
    borderRightColor: '#E5E7EB',
  },
  sidebarTop: {
    gap: 20,
  },
  sidebarHeader: {
    gap: 10,
    paddingHorizontal: 8,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  brandRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  brandMark: {
    width: 36,
    height: 36,
    borderRadius: 10,
    backgroundColor: '#F3F4F6',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  brandLogo: {
    width: 28,
    height: 28,
  },
  brandText: {
    gap: 2,
  },
  brandName: {
    color: '#173A2A',
    fontFamily: Fonts.rounded,
    fontSize: 16,
    fontWeight: '800',
  },
  brandSub: {
    color: '#6B7280',
    fontSize: 12,
    fontWeight: '600',
  },
  sidebarSection: {
    gap: 8,
  },
  sidebarLabel: {
    color: '#6B7280',
    fontSize: 11,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.8,
    paddingHorizontal: 8,
  },
  navList: {
    gap: 4,
  },
  sidebarItem: {
    minHeight: 38,
    borderRadius: 10,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingHorizontal: 8,
    backgroundColor: 'transparent',
  },
  sidebarItemActive: {
    backgroundColor: '#F3F4F6',
  },
  sidebarItemPressed: {
    opacity: 0.78,
  },
  sidebarItemIndicator: {
    width: 3,
    height: 16,
    borderRadius: 999,
    backgroundColor: 'transparent',
  },
  sidebarItemIndicatorActive: {
    backgroundColor: '#1F5A37',
  },
  sidebarIconWrap: {
    width: 18,
    height: 18,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'transparent',
  },
  sidebarIconWrapActive: {
    backgroundColor: 'transparent',
  },
  sidebarLabelText: {
    flex: 1,
    color: '#374151',
    fontSize: 14,
    fontWeight: '500',
  },
  sidebarLabelActive: {
    color: '#173A2A',
    fontWeight: '800',
  },
  sidebarFooter: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginHorizontal: 8,
    minHeight: 52,
    paddingHorizontal: 8,
    paddingTop: 14,
    paddingBottom: 6,
    borderTopWidth: 1,
    borderTopColor: '#E5E7EB',
    borderRadius: 10,
  },
  sidebarFooterActive: {
    backgroundColor: '#F3F4F6',
  },
  sidebarFooterAvatar: {
    width: 30,
    height: 30,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#1F5A37',
  },
  sidebarFooterAvatarText: {
    color: '#FFFFFF',
    fontSize: 12,
    fontWeight: '800',
    fontFamily: Fonts.rounded,
  },
  sidebarFooterPhoto: {
    width: 30,
    height: 30,
    borderRadius: 8,
    backgroundColor: '#E5E7EB',
  },
  sidebarFooterCopy: {
    flex: 1,
    gap: 2,
  },
  sidebarFooterTitle: {
    color: '#173A2A',
    fontSize: 13,
    fontWeight: '700',
  },
  sidebarFooterMeta: {
    color: '#6B7280',
    fontSize: 12,
  },
  desktopContent: {
    flex: 1,
    minWidth: 0,
    padding: 24,
    gap: 18,
  },
  desktopHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
  },
  desktopHeaderTitle: {
    color: '#173D22',
    fontSize: 26,
    fontWeight: '800',
    fontFamily: Fonts.rounded,
  },
  desktopHeaderSubtitle: {
    color: '#6A8570',
    fontSize: 12,
    fontWeight: '600',
    marginTop: 2,
  },
  desktopSlot: {
    flex: 1,
    minWidth: 0,
    borderRadius: 28,
    overflow: 'visible',
    backgroundColor: '#F4EFE6',
    borderWidth: 1,
    borderColor: '#DEE7DD',
  },
  headerSafe: {
    backgroundColor: '#EEF5EE',
  },
  header: {
    paddingHorizontal: 20,
    paddingTop: 8,
    paddingBottom: 12,
    gap: 2,
  },
  headerTitle: {
    color: '#173D22',
    fontSize: 24,
    fontWeight: '800',
    fontFamily: Fonts.rounded,
  },
  headerSubtitle: {
    color: '#6A8570',
    fontSize: 12,
    fontWeight: '600',
  },
  content: {
    flex: 1,
  },
  navSafe: {
    backgroundColor: 'transparent',
  },
  bottomNav: {
    flexDirection: 'row',
    alignItems: 'center',
    marginHorizontal: 12,
    paddingHorizontal: 8,
    paddingTop: 8,
    paddingBottom: 6,
    borderRadius: 28,
    backgroundColor: 'rgba(255, 255, 255, 0.98)',
    borderWidth: 1,
    borderColor: '#DEE8DF',
    shadowColor: '#0E2817',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.08,
    shadowRadius: 16,
    elevation: 18,
  },
  navItem: {
    flex: 1,
    minHeight: 58,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    borderRadius: 20,
    paddingVertical: 6,
  },
  navItemActive: {
    backgroundColor: '#E8F5E9',
  },
  navItemPressed: {
    opacity: 0.88,
  },
  navLabel: {
    fontSize: 9,
    fontWeight: '700',
    color: '#91A394',
    fontFamily: Fonts.rounded,
  },
  navLabelActive: {
    color: '#173D22',
  },
  pressed: {
    opacity: 0.82,
    transform: [{ scale: 0.99 }],
  },
});
